/* v2 category and search pages (category lane, RET-252, RET-253, RET-256).
   Loaded with defer by sections/main-collection.liquid and sections/main-search.liquid.
   - collection-sort: a sort change reloads the page with sort_by (page 1).
   - collection-filters: the Filter drawer form (batched ticks, live count, apply, clear).
   - The drawer controller: opens M3.3's dialog, returns focus, restores scroll.
   - Show more: appends the next page's cards (Section Rendering API), 12 per step on
     phones and 24 on wider screens, and keeps the page links underneath current. */

(() => {
  /* --- Sort --- */
  class CollectionSort extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.querySelector('[data-sort-select]')?.addEventListener('change', (event) => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', event.target.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      }, { signal: this.abort.signal });
    }

    disconnectedCallback() {
      this.abort?.abort();
    }
  }
  if (!customElements.get('collection-sort')) {
    customElements.define('collection-sort', CollectionSort);
  }

  /* --- Filters (the form inside the drawer) --- */
  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.dialog = this.closest('[data-filters-dialog]');
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.form?.addEventListener('change', () => this.#onFilterChange(), { signal });
      // Show results is the form's submit button (form attribute); a plain submit, Enter in
      // a price field included, applies the choices with sort_by kept and page dropped.
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.apply();
      }, { signal });
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.countTimer);
      this.countController?.abort();
    }

    #onFilterChange() {
      // Inside the open drawer, batch instead of navigating: reloading on every tick would
      // close the drawer. Show results commits them together.
      if (this.dialog?.open) {
        this.#scheduleCount();
        return;
      }
      this.apply();
    }

    /* M3.14: while the drawer is open, the apply button counts the results the pending
       choices would give: a debounced, abortable Section Rendering request for this
       section, whose server-rendered (pluralized) label is copied into the button and a
       status line. */
    #scheduleCount() {
      clearTimeout(this.countTimer);
      this.countTimer = setTimeout(() => this.#fetchCount(), 350);
    }

    async #fetchCount() {
      const button = this.dialog?.querySelector('[data-filters-apply]');
      const status = this.dialog?.querySelector('[data-filters-status]');
      const sectionId = this.dialog?.dataset.sectionId;
      if (!button || !sectionId) return;
      this.countController?.abort();
      const controller = new AbortController();
      this.countController = controller;
      const url = this.#pendingUrl();
      url.searchParams.set('section_id', sectionId);
      button.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(url.toString(), { signal: controller.signal, headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error(`Count request failed: ${res.status}`);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const label = doc.querySelector('[data-filters-apply]')?.textContent.trim();
        if (!label) throw new Error('No count in the section response');
        button.textContent = label;
        if (status) status.textContent = label;
      } catch (error) {
        if (error.name === 'AbortError') return;
        button.textContent = button.dataset.labelFallback || button.textContent;
        if (status) status.textContent = '';
      } finally {
        if (this.countController === controller) button.removeAttribute('aria-busy');
      }
    }

    /* Called when the drawer opens (store the label) and when it closes without applying
       (restore it and drop any pending request). */
    rememberCount() {
      const button = this.dialog?.querySelector('[data-filters-apply]');
      if (button) this.initialCountLabel = button.textContent;
    }

    cancelCount() {
      clearTimeout(this.countTimer);
      this.countController?.abort();
      const button = this.dialog?.querySelector('[data-filters-apply]');
      const status = this.dialog?.querySelector('[data-filters-status]');
      if (button) {
        button.removeAttribute('aria-busy');
        if (this.initialCountLabel) button.textContent = this.initialCountLabel;
      }
      if (status) status.textContent = '';
    }

    #pendingUrl() {
      const url = this.#urlWithoutFilters();
      for (const [name, value] of new FormData(this.form).entries()) {
        if (value) url.searchParams.append(name, value);
      }
      return url;
    }

    // Rebuild the URL from the form's checked values. Non-filter params such as sort_by and
    // q survive; page is dropped because the result set changed.
    apply() {
      if (!this.form) return;
      this.committing = true;
      clearTimeout(this.countTimer);
      this.countController?.abort();
      window.location.href = this.#pendingUrl().toString();
    }

    /* Clear all is a link to the page without filters; this only stops the close handler
       from resetting the form while the page leaves. */
    markCommitting() {
      this.committing = true;
    }

    /* M3.3: closing the drawer without Show results or Clear all drops unapplied ticks. */
    resetUnapplied() {
      if (this.committing) return;
      this.form?.reset();
      this.cancelCount();
    }

    #urlWithoutFilters() {
      const url = new URL(window.location.href);
      for (const key of [...url.searchParams.keys()]) {
        if (key.startsWith('filter.')) url.searchParams.delete(key);
      }
      url.searchParams.delete('page');
      url.hash = '';
      return url;
    }
  }
  if (!customElements.get('collection-filters')) {
    customElements.define('collection-filters', CollectionFilters);
  }

  /* --- Drawer controller (M3.3, RET-109) ---
     The filters sit in a <dialog>, so the browser handles focus trapping, Escape and the
     backdrop. Focus returns to Filter on close (WCAG 2.4.3). The drawer slides out before it
     closes (window.DialogMotion, assets/theme.js); without that script it closes at once. */
  (() => {
    if (window.v2FilterDrawerReady) return;
    window.v2FilterDrawerReady = true;
    const getDialog = () => document.querySelector('[data-filters-dialog]');
    const getFilters = () => getDialog()?.querySelector('collection-filters');
    const closeDrawer = (dialog) => (window.DialogMotion ? window.DialogMotion.close(dialog) : dialog.close());
    let lastTrigger = null;

    const setExpanded = (value) => {
      document.querySelectorAll('[data-filters-open]').forEach((opener) => {
        opener.setAttribute('aria-expanded', value ? 'true' : 'false');
      });
    };

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-filters-open]');
      if (opener) {
        const dialog = getDialog();
        if (!dialog || dialog.open) return;
        event.preventDefault();
        lastTrigger = opener;
        getFilters()?.rememberCount();
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
        document.body.style.overflow = 'hidden';
        setExpanded(true);
        return;
      }

      const dialog = getDialog();
      if (!dialog || !dialog.open) return;

      // Backdrop click: the dialog itself is the target when the click misses the panel.
      if (event.target === dialog || event.target.closest('[data-filters-close]')) {
        closeDrawer(dialog);
        return;
      }
      if (event.target.closest('[data-filters-clear]')) {
        getFilters()?.markCommitting();
      }
    });

    // Escape slides the drawer out like the close button. A request the browser will not let
    // the page hold closes at once. 'cancel' does not bubble, hence the capture phase.
    document.addEventListener(
      'cancel',
      (event) => {
        if (!event.target.matches?.('[data-filters-dialog]') || !event.cancelable) return;
        event.preventDefault();
        closeDrawer(event.target);
      },
      true
    );

    // Fires for Escape and for close() alike, so scroll, focus and the unapplied ticks are
    // always restored. 'close' does not bubble, hence the capture phase.
    document.addEventListener(
      'close',
      (event) => {
        if (!event.target.matches?.('[data-filters-dialog]')) return;
        document.body.style.overflow = '';
        setExpanded(false);
        getFilters()?.resetUnapplied();
        if (lastTrigger && lastTrigger.isConnected) lastTrigger.focus();
      },
      true
    );
  })();

  /* --- Show more ---
     Reveals the next step of cards: hidden cards already on the page first (phones show 12
     of each 24), then the next page fetched through the Section Rendering API. Focus moves
     to the first new card and the Showing line (a polite live region) announces the count.
     The page links underneath are replaced with the fetched page's, so they stay correct. */
  class CollectionMore {
    static phone = window.matchMedia('(max-width: 749px)');

    constructor(root) {
      this.root = root;
      this.section = root.closest('[data-v2-collection]');
      this.grid = this.section?.querySelector('[data-collection-grid]');
      this.button = root.querySelector('[data-show-more]');
      this.status = root.querySelector('[data-more-status]');
      if (!this.grid || !this.button || !this.status) return;
      this.sectionId = root.dataset.sectionId;
      this.start = Number(root.dataset.start) || 1;
      this.total = Number(root.dataset.total) || 0;
      this.nextUrl = this.button.dataset.nextUrl || '';
      this.visible = this.#items().length;
      if (CollectionMore.phone.matches) this.#clip(12);
      this.#sync();
      // Polite from here on, so the first sync is not announced as the page loads.
      this.status.setAttribute('role', 'status');
      this.button.addEventListener('click', () => this.more());
      CollectionMore.phone.addEventListener('change', (event) => {
        if (!event.matches) this.#clip(this.#items().length);
        this.#sync();
      });
    }

    #items() {
      return [...this.grid.children];
    }

    #step() {
      return CollectionMore.phone.matches ? 12 : 24;
    }

    #clip(limit) {
      this.visible = Math.min(limit, this.#items().length);
      this.#items().forEach((item, index) => {
        item.hidden = index >= this.visible;
      });
    }

    #sync() {
      const hiddenCount = this.#items().length - this.visible;
      const more = hiddenCount > 0 || Boolean(this.nextUrl);
      this.button.hidden = !more;
      // The page links would skip cards still held back on this page, so they wait until
      // every card here is shown. They stay in the DOM for crawlers and no-JS shoppers.
      const pages = this.root.querySelector('[data-collection-pages]');
      if (pages) pages.hidden = hiddenCount > 0;
      const end = Math.min(this.start - 1 + this.visible, this.total || Infinity);
      const template = this.start === 1 ? this.root.dataset.statusTemplate : this.root.dataset.statusRangeTemplate;
      this.status.textContent = template
        .replace('[start]', this.start)
        .replace('[end]', end)
        .replace('[total]', this.total);
      // Stays visible once Show more has been used, so the last count is still read out.
      this.status.hidden = !(more || this.used || this.start > 1 || end < this.total);
    }

    async more() {
      if (this.busy) return;
      const before = this.visible;
      this.used = true;
      const target = before + this.#step();
      if (target > this.#items().length && this.nextUrl) {
        this.busy = true;
        this.button.setAttribute('aria-busy', 'true');
        this.button.disabled = true;
        try {
          await this.#fetchNext();
        } catch (error) {
          this.status.textContent = this.root.dataset.error;
          return;
        } finally {
          this.busy = false;
          this.button.removeAttribute('aria-busy');
          this.button.disabled = false;
        }
      }
      this.#clip(target);
      this.#sync();
      const first = this.#items()[before];
      const focusTarget = first?.querySelector('.product-card__link') || first?.querySelector('a[href], button');
      focusTarget?.focus();
    }

    async #fetchNext() {
      const url = new URL(this.nextUrl, window.location.href);
      url.searchParams.set('section_id', this.sectionId);
      const res = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(`Show more failed: ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const grid = doc.querySelector('[data-collection-grid]');
      if (!grid) throw new Error('No grid in the section response');
      const fragment = document.createDocumentFragment();
      [...grid.children].forEach((item) => {
        item.hidden = true;
        fragment.append(document.importNode(item, true));
      });
      this.grid.append(fragment);
      this.nextUrl = doc.querySelector('[data-show-more]')?.dataset.nextUrl || '';
      const pages = doc.querySelector('[data-collection-pages]');
      const currentPages = this.root.querySelector('[data-collection-pages]');
      if (pages && currentPages) currentPages.replaceWith(document.importNode(pages, true));
    }
  }

  document.querySelectorAll('[data-collection-more]').forEach((root) => {
    if (!root.v2More) root.v2More = new CollectionMore(root);
  });
})();
